import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAppSelector from "./useAppSelector";
import useAppDispatch from "./useAppDispatch";
import { stopImpersonation, setIsAdminView } from "../store/authSlice";

export const useImpersonationRefresh = () => {
    const { isImpersonating, sessionId, originalUser } = useAppSelector((state) => state.auth);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isImpersonating) return;

        // Store original user data immediately when hook mounts (during impersonation)
        if (originalUser) {
            sessionStorage.setItem("refreshOriginalUser", JSON.stringify(originalUser));
        }

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            // Show browser confirmation dialog
            e.preventDefault();
            e.returnValue = "You are in impersonation mode. Refreshing will exit the session. Continue?";
            return e.returnValue;
        };

        const handleUnload = async () => {
            try {
                // Call exit-impersonation API
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                
                // Use sendBeacon for reliable delivery during unload
                const data = JSON.stringify({ sessionId });
                const blob = new Blob([data], { type: "application/json" });
                navigator.sendBeacon(
                    `${backendUrl}/admin/exit-impersonation`,
                    blob
                );
            } catch (error) {
                console.error("Error during impersonation cleanup:", error);
            }

            // Clear impersonation state
            dispatch(stopImpersonation());

            // Check if original user should return to admin view
            if (originalUser?.adminRole === "CREATOR") {
                dispatch(setIsAdminView(true));
                sessionStorage.setItem("isAdminView", "true");
            }
        };

        // Add event listeners
        window.addEventListener("beforeunload", handleBeforeUnload);
        window.addEventListener("unload", handleUnload);

        // Cleanup
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            window.removeEventListener("unload", handleUnload);
        };
    }, [isImpersonating, sessionId, dispatch, navigate, originalUser]);
};
